import React, {useRef, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {useAccountSettingsState} from 'app/settings/api';
import {DEFAULT_THUMBNAIL, generateEmbedlyOembed} from 'app/utils/embedly';
import {gt} from 'app/utils/i18n';
import {reportToSentry} from 'app/utils/sentry';
import {markAsSafe} from 'app/utils/strings';

import type {AttachedMedia} from 'sweet-types';

const STATUS_CLASSES = {
  ready: 'fa-play',
  loading: 'fa-circle-notch fa-spin',
  error: '',
  loaded: '',
} as const;

interface Props {
  video: AttachedMedia;
  isVisible: boolean;
}

type Status = {thumbError?: boolean} & (
  | {
      status: 'loading' | 'ready' | 'error' | '404';
    }
  | {status: 'loaded'; videoHTML: string}
);

export function EmbedlyVideoThumbnail({video, isVisible}: Props): JSX.Element {
  const {EMBEDLY_KEY, STATIC_URL} = useAccountSettingsState().data!;

  const [status, setStatus] = useState<Status>({
    status: 'ready',
  });

  const videoContainerRef = useRef<HTMLDivElement>(null);

  function playVideo() {
    setStatus({status: 'loading'});
    loadVideoPlayer();
  }

  function loadVideoPlayer() {
    if (!('embed_ref' in video) || video.embed_ref == null) {
      return;
    }

    generateEmbedlyOembed(video.embed_ref, EMBEDLY_KEY, {
      autoplay: true,
      maxwidth: calculateVideoWidth(),
    }).then(
      (video) => {
        if (video.type === 'video') {
          setStatus({
            status: 'loaded',
            videoHTML: video.html,
          });
        }
      },
      (error) => {
        setStatus({
          status: error.response?.status === 404 ? '404' : 'error',
        });

        if (error.response.status !== 404) {
          reportToSentry(
            'Error requesting Embedly Oembed',
            {
              response: error,
              objectURL: video.embed_ref,
              video,
            },
            true
          );
        }
      }
    );
  }

  function calculateVideoWidth(): number {
    const container = videoContainerRef.current;
    if (container) {
      if (container.getBoundingClientRect) {
        return Math.floor(container.getBoundingClientRect().width);
      }
    }

    return 1000;
  }

  const getStatusClass = () => STATUS_CLASSES[status.status];

  const getWidth = () =>
    status.status === 'loaded'
      ? {
          width: '100%',
          maxWidth: '1024px',
        }
      : undefined;

  function handleThumbnailError() {
    if (!status.thumbError) {
      setStatus({
        ...status,
        thumbError: true,
      });
      reportToSentry(
        'Unable to load video thumbnail from Embedly',
        {
          thumbnailURL: video.embed_thumb,
          videoData: video,
        },
        true
      );
    }
  }

  const thumbURL =
    !status.thumbError && Boolean(video.embed_thumb)
      ? video.embed_thumb
      : `${STATIC_URL}${DEFAULT_THUMBNAIL}`;

  return (
    <StyledContainerDIV style={getWidth()} ref={videoContainerRef}>
      <div className="video video-play-action">
        {status.status === 'loaded' && isVisible ? (
          <div dangerouslySetInnerHTML={markAsSafe(status.videoHTML)} />
        ) : status.status === 'error' || status.status === '404' ? (
          <div>
            <div className="error">
              {status.status === '404'
                ? gt('This video is no longer available (404)')
                : gt('Could not load the video')}
              {status.status === 'error' ? (
                <div>
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={playVideo}
                  >
                    {gt('Try again')}
                  </button>
                </div>
              ) : null}
            </div>
            <div
              className="view-video-thumb"
              style={{
                background: '#000',
                width: 500,
                height: 281,
              }}
            />
          </div>
        ) : (
          <div>
            <i
              className={`do-play fa ${getStatusClass()}`}
              onClick={playVideo}
            />
            <img
              className="view-video-thumb"
              src={thumbURL}
              onError={handleThumbnailError}
            />
          </div>
        )}
      </div>
    </StyledContainerDIV>
  );
}

const StyledContainerDIV = styled.div`
  text-align: center;

  .video-play-action {
    display: inline-block;
    position: relative;
    max-width: 100%;

    .do-play,
    .error {
      position: absolute;
      top: 45%;
      text-align: center;
      left: 0;
      right: 0;
    }

    .do-play {
      color: ${colors.linkBlue};
      font-size: 40px;
      cursor: pointer;

      text-shadow: 0 0 2px #000;

      &:hover {
        color: white;
      }
    }

    .error {
      color: #f9bbbf;
      font-size: 18px;
    }

    img.view-video-thumb {
      width: 480px;
      max-width: 100%;
    }
  }

  .embedly-embed {
    max-width: 100%;
  }
`;
